import React, { useEffect, useState, useRef, useMemo } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { money, dateEs } from "@/lib/formatters";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  DollarSign, TrendingUp, TrendingDown, AlertCircle, Loader2, Plus, Upload,
  ScanText, Trash2, Check, X, FileText, Users, Wallet, Receipt, ShoppingCart, Camera, Pencil,
} from "lucide-react";

const CATEGORIES = ["materiales", "transporte", "herramientas", "servicios", "combustible", "otro"];
const PAY_METHODS = ["efectivo", "transferencia", "tarjeta", "cheque", "otro"];

const KPI = ({ icon: Icon, label, value, tone = "primary", sub }) => {
  const toneClass = {
    primary: "text-[#005A91] bg-[#005A91]/10",
    success: "text-emerald-700 bg-emerald-100",
    danger: "text-red-700 bg-red-100",
    warn: "text-amber-700 bg-amber-100",
  }[tone];
  return (
    <div className="pe-card p-5">
      <div className={`h-10 w-10 rounded-md flex items-center justify-center ${toneClass}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="text-[10px] font-black tracking-[0.2em] uppercase text-slate-500 mt-3">{label}</div>
      <div className="text-2xl font-black pe-numeric mt-1">{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-1">{sub}</div>}
    </div>
  );
};

// ============ DASHBOARD ============
function DashboardTab({ onGoTo }) {
  const [range, setRange] = useState("30");
  const [sum, setSum] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/finance/summary?days=${range}`);
      setSum(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar el resumen"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [range]); // eslint-disable-line react-hooks/exhaustive-deps
  if (loading || !sum) return <div className="py-16 text-center text-slate-400"><Loader2 className="animate-spin inline mr-2" />Cargando resumen…</div>;

  return (
    <div className="space-y-6" data-testid="finance-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-slate-500">Últimos <b className="text-[#005A91]">{sum.period_days} días</b></div>
        <Select value={range} onValueChange={setRange}>
          <SelectTrigger className="w-40 h-9" data-testid="finance-range"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
            <SelectItem value="365">Último año</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI icon={TrendingUp} tone="success" label="Ingresos" value={money(sum.income_total)} sub={`${sum.income_count} registros`} />
        <KPI icon={TrendingDown} tone="danger" label="Gastos validados" value={money(sum.expense_total)} sub={`${sum.expense_count} tickets`} />
        <KPI icon={Wallet} tone={sum.cash_flow >= 0 ? "success" : "danger"} label="Flujo neto" value={money(sum.cash_flow)} sub={sum.cash_flow >= 0 ? "En positivo" : "En rojo"} />
        <KPI icon={AlertCircle} tone="warn" label="Gastos por revisar" value={money(sum.pending_expenses_total)} sub={`${sum.pending_expenses_count} pendientes`} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="pe-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#005A91]"><Users className="inline h-4 w-4 mr-1.5" />Top clientes por ingresos</h3>
          </div>
          {sum.top_clients?.length ? (
            <div className="space-y-2">
              {sum.top_clients.map((c, i) => (
                <div key={i} className="flex justify-between text-sm py-1 border-b border-slate-100 last:border-0">
                  <span className="truncate">{i + 1}. {c.name}</span>
                  <span className="pe-numeric font-bold text-emerald-700">{money(c.amount)}</span>
                </div>
              ))}
            </div>
          ) : <p className="text-sm text-slate-400">Sin ingresos en este período.</p>}
        </div>

        <div className="pe-card p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-bold text-[#005A91]"><FileText className="inline h-4 w-4 mr-1.5" />Por cobrar (aceptadas)</h3>
          </div>
          <div className="text-4xl font-black pe-numeric text-[#005A91]">{money(sum.pending_quotes_total)}</div>
          <div className="text-sm text-slate-500 mt-1">{sum.pending_quotes_count} cotizaciones aceptadas sin pago aún registrado.</div>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <Button onClick={() => onGoTo("expenses")} className="h-14 bg-[#005A91] hover:bg-[#004a7a] text-white font-bold" data-testid="dashboard-go-expenses">
          <Receipt className="h-4 w-4 mr-2" /> Registrar gasto (OCR)
        </Button>
        <Button onClick={() => onGoTo("income")} className="h-14 bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="dashboard-go-income">
          <Plus className="h-4 w-4 mr-2" /> Registrar ingreso
        </Button>
        <Button onClick={() => onGoTo("payroll")} className="h-14 bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold" data-testid="dashboard-go-payroll">
          <Users className="h-4 w-4 mr-2" /> Registrar nómina
        </Button>
      </div>
    </div>
  );
}

// ============ EXPENSES (with OCR) ============
function ExpensesTab() {
  const [expenses, setExpenses] = useState([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const q = statusFilter !== "all" ? `?status=${statusFilter}` : "";
      const r = await api.get(`/expenses${q}`);
      setExpenses(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/expenses/${id}`, { status });
      toast.success(status === "validado" ? "Gasto validado" : "Gasto rechazado");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const del = async (id) => {
    if (!confirm("¿Eliminar este gasto?")) return;
    try { await api.delete(`/expenses/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="finance-expenses">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-56 h-10" data-testid="expenses-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los estados</SelectItem>
              <SelectItem value="pendiente_revision">Pendiente de revisión</SelectItem>
              <SelectItem value="validado">Validado</SelectItem>
              <SelectItem value="rechazado">Rechazado</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={() => setShowNew(true)} className="bg-[#2E75B5] hover:bg-[#3a88cd] font-bold" data-testid="expense-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo gasto
        </Button>
      </div>

      <div className="pe-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wider">
              <th className="px-3 py-3">Fecha</th>
              <th className="px-3 py-3">Proveedor / Folio</th>
              <th className="px-3 py-3">Categoría</th>
              <th className="px-3 py-3 text-right">Total</th>
              <th className="px-3 py-3">Estado</th>
              <th className="px-3 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400"><Loader2 className="animate-spin inline mr-2" />Cargando…</td></tr>
            ) : expenses.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-12 text-slate-400">Sin gastos registrados en este filtro.</td></tr>
            ) : expenses.map((e) => (
              <tr key={e.id} data-testid={`expense-row-${e.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                <td className="px-3 py-3">{dateEs(e.date || e.created_at)}</td>
                <td className="px-3 py-3">
                  <div className="font-bold">{e.supplier || "—"}</div>
                  {e.folio && <div className="text-xs text-slate-500">Folio: {e.folio}</div>}
                </td>
                <td className="px-3 py-3 capitalize text-slate-600">{e.category}</td>
                <td className="px-3 py-3 text-right pe-numeric font-bold">{money(e.total || 0)}</td>
                <td className="px-3 py-3">
                  <StatusBadge status={e.status} />
                </td>
                <td className="px-3 py-3 text-right">
                  <div className="inline-flex gap-1">
                    {e.status === "pendiente_revision" && (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(e.id, "validado")}
                                data-testid={`expense-validate-${e.id}`}
                                className="h-8 px-2 text-emerald-700 hover:bg-emerald-50">
                          <Check className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => updateStatus(e.id, "rechazado")}
                                data-testid={`expense-reject-${e.id}`}
                                className="h-8 px-2 text-red-700 hover:bg-red-50">
                          <X className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => del(e.id)}
                            data-testid={`expense-delete-${e.id}`}
                            className="h-8 px-2 text-slate-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ExpenseNewDialog open={showNew} onOpenChange={setShowNew} onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    pendiente_revision: { label: "Por revisar", cls: "bg-amber-50 text-amber-700 border-amber-300" },
    validado: { label: "Validado", cls: "bg-emerald-50 text-emerald-700 border-emerald-300" },
    rechazado: { label: "Rechazado", cls: "bg-red-50 text-red-700 border-red-300" },
  };
  const s = map[status] || { label: status || "—", cls: "bg-slate-100 text-slate-700 border-slate-300" };
  return <span className={`inline-flex px-2 py-0.5 rounded text-xs font-bold border ${s.cls}`}>{s.label}</span>;
}

function ExpenseNewDialog({ open, onOpenChange, onCreated }) {
  const [step, setStep] = useState("photo"); // photo | manual | ocr-running
  const [photoDataUrl, setPhotoDataUrl] = useState("");
  const [ocrRunning, setOcrRunning] = useState(false);
  const [form, setForm] = useState({
    date: "", supplier: "", folio: "", category: "otro",
    subtotal: "", iva: "", total: "", payment_method: "transferencia", notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [ocrRaw, setOcrRaw] = useState(null);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  const reset = () => {
    setStep("photo"); setPhotoDataUrl(""); setOcrRunning(false); setOcrRaw(null);
    setForm({ date: "", supplier: "", folio: "", category: "otro",
              subtotal: "", iva: "", total: "", payment_method: "transferencia", notes: "" });
  };

  const readImage = (file) => new Promise((res, rej) => {
    const reader = new FileReader();
    reader.onload = () => res(reader.result);
    reader.onerror = rej;
    reader.readAsDataURL(file);
  });

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Imagen demasiado grande (máx 8MB)");
    const dataUrl = await readImage(file);
    setPhotoDataUrl(dataUrl);
    setOcrRunning(true); setStep("ocr-running");
    try {
      const r = await api.post("/expenses/ocr", { image: dataUrl });
      const p = r.data || {};
      if (p.error) toast.warning(`OCR: ${p.error}`);
      setForm((f) => ({
        ...f,
        date: p.date || f.date,
        supplier: p.supplier || f.supplier,
        folio: p.folio || f.folio,
        category: p.category || f.category,
        subtotal: p.subtotal != null ? String(p.subtotal) : f.subtotal,
        iva: p.iva != null ? String(p.iva) : f.iva,
        total: p.total != null ? String(p.total) : f.total,
        payment_method: p.payment_method || f.payment_method,
      }));
      setOcrRaw(p);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "OCR falló, puedes capturar manual");
    } finally { setOcrRunning(false); setStep("manual"); }
  };

  const skipToManual = () => { setStep("manual"); };

  const save = async () => {
    if (!form.total || Number(form.total) <= 0) return toast.error("Falta el total");
    setSaving(true);
    try {
      const body = {
        date: form.date ? new Date(form.date).toISOString() : undefined,
        supplier: form.supplier || undefined,
        folio: form.folio || undefined,
        category: form.category,
        subtotal: form.subtotal ? Number(form.subtotal) : undefined,
        iva: form.iva ? Number(form.iva) : undefined,
        total: Number(form.total),
        payment_method: form.payment_method,
        photo_data_url: photoDataUrl || undefined,
        notes: form.notes || undefined,
        ocr_raw: ocrRaw || undefined,
      };
      await api.post("/expenses", body);
      toast.success("Gasto registrado como pendiente de revisión");
      reset(); onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="expense-new-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#005A91] font-black">
            {step === "photo" ? "Nuevo gasto — Foto del ticket" :
             step === "ocr-running" ? "Escaneando ticket con IA…" :
             "Nuevo gasto — Revisa los datos"}
          </DialogTitle>
          <DialogDescription>
            {step === "photo" && "Sube una foto legible del ticket o factura. La IA extraerá los datos automáticamente. También puedes capturarlos manual."}
            {step === "ocr-running" && "Esto tarda unos segundos. Verás los campos precargados al terminar."}
            {step === "manual" && "Los datos entran como pendiente de revisión y solo cuentan al validarlos."}
          </DialogDescription>
        </DialogHeader>

        {step === "photo" && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="cursor-pointer rounded-md border-2 border-[#005A91] bg-[#005A91] text-white p-5 flex flex-col items-center gap-2 hover:bg-[#004a7a] transition"
                     data-testid="expense-photo-camera">
                <Camera className="h-8 w-8" strokeWidth={2.2} />
                <div className="font-black text-base">Tomar foto ahora</div>
                <div className="text-[11px] opacity-80 text-center">Abre la cámara del móvil</div>
                <input ref={cameraInputRef} type="file" accept="image/*" capture="environment"
                       className="hidden" onChange={onPhoto} data-testid="expense-camera-input" />
              </label>
              <label className="cursor-pointer rounded-md border-2 border-dashed border-[#005A91]/40 bg-white p-5 flex flex-col items-center gap-2 hover:bg-slate-50 transition"
                     data-testid="expense-photo-gallery">
                <Upload className="h-8 w-8 text-[#005A91]" strokeWidth={2.2} />
                <div className="font-black text-base text-[#005A91]">Subir desde galería</div>
                <div className="text-[11px] text-slate-500 text-center">JPG, PNG · máx 8MB</div>
                <input ref={galleryInputRef} type="file" accept="image/*"
                       className="hidden" onChange={onPhoto} data-testid="expense-gallery-input" />
              </label>
            </div>
            <Button variant="ghost" onClick={skipToManual} className="w-full text-slate-600" data-testid="expense-skip-ocr">
              Capturar sin foto (manual)
            </Button>
          </div>
        )}
        {step === "ocr-running" && (
          <div className="py-8 text-center">
            <ScanText className="mx-auto h-12 w-12 text-[#005A91] animate-pulse" />
            <div className="mt-3 text-sm text-slate-600">Extrayendo datos…</div>
          </div>
        )}
        {step === "manual" && (
          <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
            {photoDataUrl && (
              <div className="rounded-md overflow-hidden border border-slate-200">
                <img src={photoDataUrl} alt="ticket" className="max-h-40 w-full object-contain bg-slate-100" />
              </div>
            )}
            {ocrRaw && ocrRaw.confidence != null && (
              <div className="text-xs bg-slate-50 border border-slate-200 rounded px-3 py-2 text-slate-600">
                IA confianza: <b>{Math.round((ocrRaw.confidence || 0) * 100)}%</b> — revisa antes de guardar.
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <Field label="Fecha"><Input type="date" value={(form.date || "").slice(0, 10)}
                                             onChange={(e) => setForm({ ...form, date: e.target.value })}
                                             data-testid="expense-date" /></Field>
              <Field label="Categoría">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="expense-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Proveedor" full><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} data-testid="expense-supplier" /></Field>
              <Field label="Folio / Referencia"><Input value={form.folio} onChange={(e) => setForm({ ...form, folio: e.target.value })} data-testid="expense-folio" /></Field>
              <Field label="Método de pago">
                <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                  <SelectTrigger data-testid="expense-pay-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Subtotal"><Input type="number" step="0.01" value={form.subtotal} onChange={(e) => setForm({ ...form, subtotal: e.target.value })} data-testid="expense-subtotal" /></Field>
              <Field label="IVA"><Input type="number" step="0.01" value={form.iva} onChange={(e) => setForm({ ...form, iva: e.target.value })} data-testid="expense-iva" /></Field>
              <Field label="Total *" full><Input type="number" step="0.01" required value={form.total} onChange={(e) => setForm({ ...form, total: e.target.value })} data-testid="expense-total" className="text-lg font-bold" /></Field>
              <Field label="Notas" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="expense-notes" /></Field>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          {step === "manual" && (
            <Button onClick={save} disabled={saving} data-testid="expense-save"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Guardar como pendiente
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const Field = ({ label, children, full }) => (
  <div className={`space-y-1 ${full ? "col-span-2" : ""}`}>
    <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</Label>
    {children}
  </div>
);

// ============ INCOME ============
function IncomeTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [form, setForm] = useState({ concept: "", client_name: "", amount: "", payment_method: "transferencia", reference: "", notes: "", date: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/incomes"); setItems(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.concept || !form.amount) return toast.error("Concepto y monto obligatorios");
    setSaving(true);
    try {
      await api.post("/incomes", {
        concept: form.concept,
        client_name: form.client_name || undefined,
        amount: Number(form.amount),
        payment_method: form.payment_method,
        reference: form.reference || undefined,
        notes: form.notes || undefined,
        date: form.date ? new Date(form.date).toISOString() : undefined,
      });
      toast.success("Ingreso registrado");
      setForm({ concept: "", client_name: "", amount: "", payment_method: "transferencia", reference: "", notes: "", date: "" });
      setShowNew(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!confirm("¿Eliminar ingreso?")) return;
    try { await api.delete(`/incomes/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="finance-income">
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowNew(true)} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold" data-testid="income-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nuevo ingreso
        </Button>
      </div>
      <div className="pe-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wider">
              <th className="px-3 py-3">Fecha</th>
              <th className="px-3 py-3">Concepto</th>
              <th className="px-3 py-3">Cliente / Referencia</th>
              <th className="px-3 py-3">Método</th>
              <th className="px-3 py-3 text-right">Monto</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" />Cargando…</td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-slate-400">Sin ingresos registrados.</td></tr>
              : items.map((i) => (
                <tr key={i.id} data-testid={`income-row-${i.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-3">{dateEs(i.date)}</td>
                  <td className="px-3 py-3">{i.concept}{i.quote_folio && <span className="ml-2 text-xs text-[#005A91]">({i.quote_folio})</span>}</td>
                  <td className="px-3 py-3 text-slate-600">
                    <div>{i.client_name || "—"}</div>
                    {i.reference && <div className="text-xs text-slate-400">Ref: {i.reference}</div>}
                  </td>
                  <td className="px-3 py-3 capitalize text-slate-600">{i.payment_method}</td>
                  <td className="px-3 py-3 text-right pe-numeric font-bold text-emerald-700">{money(i.amount)}</td>
                  <td className="px-3 py-3 text-right">
                    <Button size="sm" variant="ghost" onClick={() => del(i.id)} data-testid={`income-delete-${i.id}`}
                            className="h-8 px-2 text-slate-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      <Dialog open={showNew} onOpenChange={setShowNew}>
        <DialogContent className="sm:max-w-md" data-testid="income-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#005A91] font-black">Nuevo ingreso</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Fecha"><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="income-date" /></Field>
            <Field label="Método">
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="income-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Concepto *" full><Input value={form.concept} onChange={(e) => setForm({ ...form, concept: e.target.value })} data-testid="income-concept" /></Field>
            <Field label="Cliente" full><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} data-testid="income-client" /></Field>
            <Field label="Monto *"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="income-amount" className="text-lg font-bold" /></Field>
            <Field label="Referencia"><Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} data-testid="income-reference" /></Field>
            <Field label="Notas" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="income-save" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null} Guardar ingreso
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ PAYROLL ============
function AttendanceControlTab() {
  const DAYS = [
    { key: "L", label: "L" },
    { key: "Ma", label: "M" },
    { key: "Mi", label: "M" },
    { key: "J", label: "J" },
    { key: "V", label: "V" },
    { key: "S", label: "S" },
    { key: "D", label: "D" },
  ];
  const MARK_CYCLE = ["", "A", "R", "F", "O"];
  const MARK_STYLE = {
    A: "bg-emerald-100 text-emerald-700 border-emerald-200",
    R: "bg-amber-100 text-amber-700 border-amber-200",
    F: "bg-red-100 text-red-700 border-red-200",
    O: "bg-sky-100 text-sky-700 border-sky-200",
    "": "bg-slate-50 text-slate-400 border-slate-200",
  };
  const nextMark = (m) => {
    const i = MARK_CYCLE.indexOf(m || "");
    return MARK_CYCLE[(i + 1) % MARK_CYCLE.length];
  };
  // Lunes de la semana actual (local)
  const mondayOf = (d) => {
    const x = new Date(d);
    const dow = (x.getDay() + 6) % 7; // 0 = lunes
    x.setHours(0, 0, 0, 0);
    x.setDate(x.getDate() - dow);
    return x;
  };
  const toDateISO = (d) => new Date(d).toISOString().slice(0, 10);

  const [weekStart, setWeekStart] = useState(toDateISO(mondayOf(new Date())));
  const [employees, setEmployees] = useState([]); // {employee_name, employee_role, last_salario_diario}
  const [rowsByEmp, setRowsByEmp] = useState({}); // { name: {id?, salario_diario, marks} }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [newEmpOpen, setNewEmpOpen] = useState(false);
  const [newEmp, setNewEmp] = useState({ employee_name: "", employee_role: "", salario_diario: "" });
  const saveTimers = useRef({});
  // Ref siempre-al-día para evitar stale-closures en el timer debounced
  const rowsRef = useRef({});
  useEffect(() => { rowsRef.current = rowsByEmp; }, [rowsByEmp]);

  const weekStartISO = useMemo(() => {
    // envía con tiempo 00:00Z para clave consistente
    const d = new Date(weekStart);
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, [weekStart]);

  const load = async () => {
    setLoading(true);
    try {
      const [emps, weeks] = await Promise.all([
        api.get("/attendance-employees"),
        api.get("/attendance-weeks", { params: { week_start: weekStartISO } }),
      ]);
      setEmployees(emps.data || []);
      const map = {};
      for (const w of weeks.data || []) {
        map[w.employee_name] = {
          id: w.id, salario_diario: w.salario_diario || 0, marks: w.marks || {},
          total_dias: w.total_dias, total_semanal: w.total_semanal,
        };
      }
      // Rellena empleados sin datos en la semana con default
      for (const e of emps.data || []) {
        if (!map[e.employee_name]) {
          map[e.employee_name] = {
            salario_diario: e.last_salario_diario || 0, marks: {},
            total_dias: 0, total_semanal: 0,
          };
        }
      }
      setRowsByEmp(map);
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStartISO]);

  const paidCount = (marks) => Object.values(marks || {}).filter((v) => v === "A" || v === "R").length;

  const scheduleSave = (name) => {
    if (saveTimers.current[name]) clearTimeout(saveTimers.current[name]);
    saveTimers.current[name] = setTimeout(() => doSave(name), 500);
  };

  const doSave = async (name) => {
    // Lee la fila más reciente vía ref para evitar stale-closure con el debounce
    const row = rowsRef.current[name];
    if (!row) return;
    setSaving((s) => ({ ...s, [name]: true }));
    try {
      const emp = employees.find((e) => e.employee_name === name);
      const r = await api.put("/attendance-weeks", {
        week_start: weekStartISO,
        employee_name: name,
        employee_role: emp?.employee_role || null,
        salario_diario: Number(row.salario_diario || 0),
        marks: row.marks || {},
      });
      // Sólo mergea los campos autoritativos del server (id/total_dias/total_semanal).
      // Preserva marks/salario_diario locales (el usuario puede haber tecleado más
      // caracteres mientras el PUT estaba en vuelo).
      setRowsByEmp((cur) => {
        const local = cur[name] || {};
        return { ...cur, [name]: {
          ...local,
          id: r.data.id,
          total_dias: r.data.total_dias,
          total_semanal: r.data.total_semanal,
        }};
      });
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al guardar"); }
    finally { setSaving((s) => ({ ...s, [name]: false })); }
  };

  const cycleMark = (name, dayKey) => {
    setRowsByEmp((cur) => {
      const prev = cur[name] || { salario_diario: 0, marks: {} };
      const nextMarks = { ...(prev.marks || {}), [dayKey]: nextMark(prev.marks?.[dayKey]) };
      return { ...cur, [name]: { ...prev, marks: nextMarks } };
    });
    scheduleSave(name);
  };
  const setSalario = (name, value) => {
    setRowsByEmp((cur) => {
      const prev = cur[name] || { marks: {} };
      return { ...cur, [name]: { ...prev, salario_diario: value } };
    });
    scheduleSave(name);
  };
  const clearRow = async (name) => {
    if (!confirm(`Limpiar toda la asistencia de ${name} en esta semana?`)) return;
    const prev = rowsByEmp[name];
    setRowsByEmp((cur) => ({ ...cur, [name]: { ...cur[name], marks: {}, total_dias: 0, total_semanal: 0 } }));
    // Si tiene id (existe en DB), borra directamente
    if (prev?.id) {
      try { await api.delete(`/attendance-weeks/${prev.id}`); }
      catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    }
  };
  const addEmployee = async () => {
    const name = (newEmp.employee_name || "").trim();
    if (!name) return toast.error("Nombre requerido");
    if (employees.some((e) => e.employee_name === name)) {
      return toast.error("Ya existe un empleado con ese nombre");
    }
    // Guarda directamente creando una fila vacía para la semana actual
    try {
      const r = await api.put("/attendance-weeks", {
        week_start: weekStartISO,
        employee_name: name,
        employee_role: newEmp.employee_role || null,
        salario_diario: Number(newEmp.salario_diario || 0),
        marks: {},
      });
      setEmployees((cur) => [...cur, { employee_name: name,
          employee_role: newEmp.employee_role || null,
          last_salario_diario: Number(newEmp.salario_diario || 0) }]);
      setRowsByEmp((cur) => ({ ...cur, [name]: {
        id: r.data.id, salario_diario: r.data.salario_diario, marks: r.data.marks,
        total_dias: r.data.total_dias, total_semanal: r.data.total_semanal,
      }}));
      setNewEmp({ employee_name: "", employee_role: "", salario_diario: "" });
      setNewEmpOpen(false);
      toast.success("Empleado agregado");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="finance-attendance">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <Label className="pe-label">Semana (inicia lunes)</Label>
            <Input type="date" value={weekStart}
                    onChange={(e) => setWeekStart(toDateISO(mondayOf(e.target.value)))}
                    data-testid="att-week-input"
                    className="h-10 w-44 mt-1" />
          </div>
          <div className="text-xs text-slate-500">
            <span className="mr-3"><b className="text-emerald-600">A</b> Asistencia</span>
            <span className="mr-3"><b className="text-amber-600">R</b> Retardo</span>
            <span className="mr-3"><b className="text-red-600">F</b> Falta</span>
            <span><b className="text-sky-600">O</b> Permiso</span>
          </div>
        </div>
        <Button onClick={() => setNewEmpOpen(true)}
                data-testid="att-add-employee-btn"
                className="bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
          <Plus className="h-4 w-4 mr-1.5" /> Agregar empleado
        </Button>
      </div>

      <div className="pe-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wider">
              <th className="px-3 py-3 w-12">#</th>
              <th className="px-3 py-3">Empleado</th>
              {DAYS.map((d) => <th key={d.key} className="px-1 py-3 text-center w-12">{d.label}</th>)}
              <th className="px-3 py-3 text-center">Total Días</th>
              <th className="px-3 py-3 text-right">Salario Diario</th>
              <th className="px-3 py-3 text-right">Total Semanal</th>
              <th className="px-3 py-3 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={12} className="text-center py-10 text-slate-400">
                <Loader2 className="animate-spin inline mr-2" />Cargando…</td></tr>
            ) : employees.length === 0 ? (
              <tr><td colSpan={12} className="text-center py-10 text-slate-400">
                Sin empleados. Usa "Agregar empleado" para comenzar.</td></tr>
            ) : employees.map((e, idx) => {
              const row = rowsByEmp[e.employee_name] || { salario_diario: 0, marks: {} };
              const dias = row.total_dias ?? paidCount(row.marks);
              const salario = Number(row.salario_diario || 0);
              const total = row.total_semanal ?? +(dias * salario).toFixed(2);
              return (
                <tr key={e.employee_name} data-testid={`att-row-${e.employee_name}`}
                    className="border-t border-slate-100">
                  <td className="px-3 py-2 pe-numeric text-xs text-slate-500 font-bold">
                    {String(idx + 1).padStart(3, "0")}
                  </td>
                  <td className="px-3 py-2">
                    <div className="font-bold">{e.employee_name}</div>
                    {e.employee_role && <div className="text-xs text-slate-500">{e.employee_role}</div>}
                  </td>
                  {DAYS.map((d) => {
                    const mark = row.marks?.[d.key] || "";
                    return (
                      <td key={d.key} className="px-1 py-2 text-center">
                        <button onClick={() => cycleMark(e.employee_name, d.key)}
                                data-testid={`att-cell-${e.employee_name}-${d.key}`}
                                title={`${d.key} — click para cambiar`}
                                className={`h-9 w-9 rounded-md border font-black text-sm ${MARK_STYLE[mark]} hover:brightness-95 transition`}>
                          {mark || "·"}
                        </button>
                      </td>
                    );
                  })}
                  <td className="px-3 py-2 text-center pe-numeric font-bold text-[#005A91]"
                      data-testid={`att-dias-${e.employee_name}`}>
                    {dias}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Input type="number" step="0.01" min={0}
                            value={row.salario_diario ?? ""}
                            onChange={(ev) => setSalario(e.employee_name, ev.target.value)}
                            data-testid={`att-salario-${e.employee_name}`}
                            className="h-9 w-24 text-right pe-numeric ml-auto" />
                  </td>
                  <td className="px-3 py-2 text-right pe-numeric font-black text-[#005A91]"
                      data-testid={`att-total-${e.employee_name}`}>
                    {money(total)}
                    {saving[e.employee_name] && <Loader2 className="h-3 w-3 animate-spin inline ml-1 text-slate-400" />}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <Button size="sm" variant="ghost" onClick={() => clearRow(e.employee_name)}
                            data-testid={`att-clear-${e.employee_name}`}
                            className="h-8 px-2 text-slate-500 hover:text-red-700 hover:bg-red-50"
                            title="Limpiar semana">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={newEmpOpen} onOpenChange={setNewEmpOpen}>
        <DialogContent className="sm:max-w-md" data-testid="att-emp-dialog">
          <DialogHeader><DialogTitle className="text-[#005A91] font-black">Agregar empleado</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Nombre *" full>
              <Input value={newEmp.employee_name}
                     onChange={(e) => setNewEmp({ ...newEmp, employee_name: e.target.value })}
                     data-testid="att-emp-name" />
            </Field>
            <Field label="Puesto" full>
              <Input value={newEmp.employee_role}
                     onChange={(e) => setNewEmp({ ...newEmp, employee_role: e.target.value })}
                     data-testid="att-emp-role" />
            </Field>
            <Field label="Salario diario" full>
              <Input type="number" step="0.01" value={newEmp.salario_diario}
                     onChange={(e) => setNewEmp({ ...newEmp, salario_diario: e.target.value })}
                     data-testid="att-emp-salary" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewEmpOpen(false)}>Cancelar</Button>
            <Button onClick={addEmployee} data-testid="att-emp-save"
                    className="bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
              Agregar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function PayrollTab() {
  const [view, setView] = useState("register"); // "register" | "attendance"
  return (
    <div className="space-y-4" data-testid="finance-payroll-host">
      <div className="pe-card p-1 inline-flex gap-1" data-testid="payroll-view-toggle">
        <button onClick={() => setView("register")}
                data-testid="payroll-tab-register"
                className={`px-4 py-1.5 rounded-md text-sm font-bold transition ${
                  view === "register" ? "bg-[#005A91] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
          Registro de nómina
        </button>
        <button onClick={() => setView("attendance")}
                data-testid="payroll-tab-attendance"
                className={`px-4 py-1.5 rounded-md text-sm font-bold transition ${
                  view === "attendance" ? "bg-[#005A91] text-white" : "text-slate-600 hover:bg-slate-100"
                }`}>
          Control de asistencia
        </button>
      </div>
      {view === "register" ? <PayrollRegisterTab /> : <AttendanceControlTab />}
    </div>
  );
}

function PayrollRegisterTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const todayISO = () => new Date().toISOString().slice(0, 10);
  const emptyForm = {
    employee_name: "", employee_role: "", period_start: "", period_end: "",
    percepciones: "", deducciones: "", payment_method: "transferencia", payment_date: "", notes: "",
    attendance_notes: [],
  };
  const [form, setForm] = useState(emptyForm);
  const [attDraft, setAttDraft] = useState({ date: todayISO(), note: "" });
  const [expanded, setExpanded] = useState({});
  const [attRow, setAttRow] = useState({}); // { [rowId]: {date, note} }
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/payroll"); setItems(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const neto = (Number(form.percepciones || 0) - Number(form.deducciones || 0)).toFixed(2);

  const resetForm = () => {
    setForm(emptyForm);
    setAttDraft({ date: todayISO(), note: "" });
    setEditingId(null);
  };

  const openEdit = (p) => {
    const toDateInput = (iso) => iso ? new Date(iso).toISOString().slice(0, 10) : "";
    setEditingId(p.id);
    setForm({
      employee_name: p.employee_name || "",
      employee_role: p.employee_role || "",
      period_start: toDateInput(p.period_start),
      period_end: toDateInput(p.period_end),
      percepciones: String(p.percepciones ?? ""),
      deducciones: String(p.deducciones ?? ""),
      payment_method: p.payment_method || "transferencia",
      payment_date: toDateInput(p.payment_date),
      notes: p.notes || "",
      attendance_notes: p.attendance_notes || [],
    });
    setShowNew(true);
  };

  const addAttToDraft = () => {
    const n = (attDraft.note || "").trim();
    if (!n) return toast.error("Escribe la nota");
    if (!attDraft.date) return toast.error("Selecciona una fecha");
    setForm((f) => ({
      ...f,
      attendance_notes: [...f.attendance_notes,
        { date: new Date(attDraft.date).toISOString(), note: n }],
    }));
    setAttDraft({ date: todayISO(), note: "" });
  };
  const removeAttFromDraft = (idx) => {
    setForm((f) => ({ ...f, attendance_notes: f.attendance_notes.filter((_, i) => i !== idx) }));
  };

  const save = async () => {
    if (!form.employee_name || !form.period_start || !form.period_end) {
      return toast.error("Empleado y periodo obligatorios");
    }
    setSaving(true);
    try {
      if (editingId) {
        await api.patch(`/payroll/${editingId}`, {
          employee_name: form.employee_name,
          employee_role: form.employee_role || null,
          period_start: new Date(form.period_start).toISOString(),
          period_end: new Date(form.period_end).toISOString(),
          percepciones: Number(form.percepciones || 0),
          deducciones: Number(form.deducciones || 0),
          payment_method: form.payment_method,
          payment_date: form.payment_date ? new Date(form.payment_date).toISOString() : null,
          notes: form.notes || null,
        });
        toast.success("Nómina actualizada");
      } else {
        await api.post("/payroll", {
          employee_name: form.employee_name,
          employee_role: form.employee_role || undefined,
          period_start: new Date(form.period_start).toISOString(),
          period_end: new Date(form.period_end).toISOString(),
          percepciones: Number(form.percepciones || 0),
          deducciones: Number(form.deducciones || 0),
          payment_method: form.payment_method,
          payment_date: form.payment_date ? new Date(form.payment_date).toISOString() : undefined,
          notes: form.notes || undefined,
          attendance_notes: form.attendance_notes,
        });
        toast.success("Registro de nómina guardado");
      }
      resetForm();
      setShowNew(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    if (!confirm("¿Eliminar entrada de nómina?")) return;
    try { await api.delete(`/payroll/${id}`); toast.success("Eliminado"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const addAttToRow = async (rowId) => {
    const draft = attRow[rowId] || {};
    const n = (draft.note || "").trim();
    if (!n) return toast.error("Escribe la nota");
    if (!draft.date) return toast.error("Selecciona una fecha");
    try {
      const r = await api.post(`/payroll/${rowId}/attendance-notes`,
                                { date: new Date(draft.date).toISOString(), note: n });
      setItems((cur) => cur.map((it) => (it.id === rowId ? r.data : it)));
      setAttRow((cur) => ({ ...cur, [rowId]: { date: todayISO(), note: "" } }));
      toast.success("Nota agregada");
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };
  const removeAttFromRow = async (rowId, index) => {
    try {
      const r = await api.delete(`/payroll/${rowId}/attendance-notes/${index}`);
      setItems((cur) => cur.map((it) => (it.id === rowId ? r.data : it)));
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="space-y-4" data-testid="finance-payroll">
      <div className="flex items-center justify-end">
        <Button onClick={() => setShowNew(true)} className="bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold" data-testid="payroll-new-btn">
          <Plus className="h-4 w-4 mr-1.5" /> Nueva nómina
        </Button>
      </div>
      <div className="pe-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wider">
              <th className="px-3 py-3">Empleado</th>
              <th className="px-3 py-3">Periodo</th>
              <th className="px-3 py-3 text-right">Percepciones</th>
              <th className="px-3 py-3 text-right">Deducciones</th>
              <th className="px-3 py-3 text-right">Neto</th>
              <th className="px-3 py-3">Pago</th>
              <th className="px-3 py-3">Asistencia</th>
              <th className="px-3 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={8} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" />Cargando…</td></tr>
              : items.length === 0 ? <tr><td colSpan={8} className="text-center py-10 text-slate-400">Sin nómina registrada.</td></tr>
              : items.map((p) => {
                  const attList = p.attendance_notes || [];
                  const isOpen = !!expanded[p.id];
                  const draft = attRow[p.id] || { date: todayISO(), note: "" };
                  return (
                <React.Fragment key={p.id}>
                <tr data-testid={`payroll-row-${p.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-3">
                    <div className="font-bold">{p.employee_name}</div>
                    {p.employee_role && <div className="text-xs text-slate-500">{p.employee_role}</div>}
                  </td>
                  <td className="px-3 py-3 text-xs text-slate-600">{dateEs(p.period_start)} — {dateEs(p.period_end)}</td>
                  <td className="px-3 py-3 text-right pe-numeric">{money(p.percepciones)}</td>
                  <td className="px-3 py-3 text-right pe-numeric text-red-700">{money(p.deducciones)}</td>
                  <td className="px-3 py-3 text-right pe-numeric font-bold text-[#005A91]">{money(p.neto)}</td>
                  <td className="px-3 py-3 capitalize text-xs text-slate-600">
                    {p.payment_method || "—"}{p.payment_date ? ` · ${dateEs(p.payment_date)}` : ""}
                  </td>
                  <td className="px-3 py-3">
                    <button onClick={() => setExpanded((e) => ({ ...e, [p.id]: !e[p.id] }))}
                            data-testid={`payroll-att-toggle-${p.id}`}
                            className="inline-flex items-center gap-1 text-xs font-bold text-[#005A91] hover:underline">
                      {attList.length > 0
                        ? `${attList.length} nota${attList.length === 1 ? "" : "s"}`
                        : "Sin notas"}
                      <span className="text-[10px]">{isOpen ? "▲" : "▼"}</span>
                    </button>
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    <Button size="sm" variant="ghost" onClick={() => openEdit(p)} data-testid={`payroll-edit-${p.id}`}
                            className="h-8 px-2 text-slate-500 hover:text-[#005A91] hover:bg-slate-100">
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(p.id)} data-testid={`payroll-delete-${p.id}`}
                            className="h-8 px-2 text-slate-500 hover:text-red-700 hover:bg-red-50">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </td>
                </tr>
                {isOpen && (
                  <tr data-testid={`payroll-att-panel-${p.id}`} className="bg-slate-50">
                    <td colSpan={8} className="px-3 py-3">
                      <div className="text-xs font-bold text-[#005A91] uppercase tracking-wider mb-2">
                        Notas de asistencia
                      </div>
                      {attList.length === 0 ? (
                        <div className="text-xs text-slate-500 mb-3">Aún no hay notas para este registro.</div>
                      ) : (
                        <ul className="space-y-1.5 mb-3">
                          {attList.map((n, i) => (
                            <li key={i}
                                data-testid={`payroll-att-item-${p.id}-${i}`}
                                className="flex items-start gap-2 text-sm">
                              <span className="pe-numeric text-xs text-slate-500 font-bold shrink-0 w-24">
                                {dateEs(n.date)}
                              </span>
                              <span className="flex-1 text-slate-700 whitespace-pre-wrap">{n.note}</span>
                              <button onClick={() => removeAttFromRow(p.id, i)}
                                      data-testid={`payroll-att-del-${p.id}-${i}`}
                                      className="text-red-400 hover:text-red-600 shrink-0"
                                      title="Eliminar nota">
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <div className="flex flex-wrap items-end gap-2">
                        <div>
                          <Label className="pe-label">Fecha</Label>
                          <Input type="date" value={draft.date}
                                 onChange={(e) => setAttRow((cur) => ({ ...cur, [p.id]: { ...draft, date: e.target.value } }))}
                                 data-testid={`payroll-att-row-date-${p.id}`}
                                 className="h-9 w-40 mt-1" />
                        </div>
                        <div className="flex-1 min-w-[200px]">
                          <Label className="pe-label">Nota</Label>
                          <Input value={draft.note}
                                 onChange={(e) => setAttRow((cur) => ({ ...cur, [p.id]: { ...draft, note: e.target.value } }))}
                                 placeholder="Ej: Llegó tarde 15 min / Falta justificada / Doble turno"
                                 data-testid={`payroll-att-row-note-${p.id}`}
                                 className="h-9 mt-1" />
                        </div>
                        <Button size="sm" onClick={() => addAttToRow(p.id)}
                                data-testid={`payroll-att-row-add-${p.id}`}
                                className="h-9 bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
                          <Plus className="h-3.5 w-3.5 mr-1" /> Agregar nota
                        </Button>
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );})}
          </tbody>
        </table>
      </div>

      <Dialog open={showNew} onOpenChange={(o) => { setShowNew(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="payroll-dialog">
          <DialogHeader>
            <DialogTitle className="text-[#005A91] font-black">
              {editingId ? "Editar nómina" : "Nueva nómina"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <Field label="Empleado *" full><Input value={form.employee_name} onChange={(e) => setForm({ ...form, employee_name: e.target.value })} data-testid="payroll-name" /></Field>
            <Field label="Puesto" full><Input value={form.employee_role} onChange={(e) => setForm({ ...form, employee_role: e.target.value })} data-testid="payroll-role" /></Field>
            <Field label="Inicio periodo *"><Input type="date" value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} data-testid="payroll-start" /></Field>
            <Field label="Fin periodo *"><Input type="date" value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} data-testid="payroll-end" /></Field>
            <Field label="Percepciones"><Input type="number" step="0.01" value={form.percepciones} onChange={(e) => setForm({ ...form, percepciones: e.target.value })} data-testid="payroll-perc" /></Field>
            <Field label="Deducciones"><Input type="number" step="0.01" value={form.deducciones} onChange={(e) => setForm({ ...form, deducciones: e.target.value })} data-testid="payroll-ded" /></Field>
            <Field label="Neto (calculado)" full>
              <div className="h-10 px-3 rounded-md border border-slate-200 bg-slate-50 flex items-center font-bold text-[#005A91] pe-numeric" data-testid="payroll-neto">
                {money(neto)}
              </div>
            </Field>
            <Field label="Método">
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="payroll-method"><SelectValue /></SelectTrigger>
                <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Fecha de pago"><Input type="date" value={form.payment_date} onChange={(e) => setForm({ ...form, payment_date: e.target.value })} data-testid="payroll-paydate" /></Field>
            <Field label="Notas" full><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></Field>
          </div>

          <div className="border-t border-slate-200 pt-3 mt-1">
            <div className="text-xs font-bold text-[#005A91] uppercase tracking-wider mb-2">
              Notas de asistencia {editingId ? "(edita desde la tabla)" : "(opcional)"}
            </div>
            {editingId ? (
              <div className="text-xs text-slate-500 mb-2">
                {form.attendance_notes.length > 0
                  ? `Este registro tiene ${form.attendance_notes.length} nota${form.attendance_notes.length === 1 ? "" : "s"}. Ábrelo desde la tabla para agregar o eliminar notas.`
                  : "Aún no hay notas para este registro. Ábrelo desde la tabla ('N notas' o 'Sin notas') para agregarlas."}
              </div>
            ) : (
              <>
                {form.attendance_notes.length > 0 && (
                  <ul className="space-y-1.5 mb-2">
                    {form.attendance_notes.map((n, i) => (
                      <li key={i} data-testid={`payroll-form-att-${i}`}
                          className="flex items-start gap-2 text-sm bg-slate-50 rounded-md px-2 py-1.5">
                        <span className="pe-numeric text-xs text-slate-500 font-bold w-24 shrink-0">
                          {dateEs(n.date)}
                        </span>
                        <span className="flex-1 text-slate-700">{n.note}</span>
                        <button onClick={() => removeAttFromDraft(i)}
                                data-testid={`payroll-form-att-del-${i}`}
                                className="text-red-400 hover:text-red-600 shrink-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap items-end gap-2">
                  <div>
                    <Label className="pe-label">Fecha</Label>
                    <Input type="date" value={attDraft.date}
                           onChange={(e) => setAttDraft((d) => ({ ...d, date: e.target.value }))}
                           data-testid="payroll-form-att-date"
                           className="h-9 w-40 mt-1" />
                  </div>
                  <div className="flex-1 min-w-[180px]">
                    <Label className="pe-label">Nota</Label>
                    <Input value={attDraft.note}
                           onChange={(e) => setAttDraft((d) => ({ ...d, note: e.target.value }))}
                           placeholder="Ej: Falta justificada"
                           data-testid="payroll-form-att-note"
                           className="h-9 mt-1" />
                  </div>
                  <Button size="sm" type="button" onClick={addAttToDraft}
                          data-testid="payroll-form-att-add"
                          className="h-9 bg-[#005A91] hover:bg-[#004a7a] text-white font-bold">
                    <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
                  </Button>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowNew(false); resetForm(); }}>Cancelar</Button>
            <Button onClick={save} disabled={saving} data-testid="payroll-save" className="bg-[#2E75B5] hover:bg-[#3a88cd] text-white font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingId ? "Guardar cambios" : "Guardar nómina"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ SALES NOTES ============
function SalesNotesTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/sales-notes"); setItems(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-4" data-testid="finance-sales-notes">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-xs text-slate-500 max-w-2xl">
          Emite una nota de venta rápida cuando cobras algo que no pasó por cotización
          (ventas de mostrador, materiales sueltos, servicios express). Se crea con folio
          <b className="text-[#005A91]"> NV-XXXX</b> y automáticamente registra un ingreso en el módulo.
        </p>
        <Button onClick={() => setShowNew(true)} className="bg-[#005A91] hover:bg-[#004a7a] text-white font-bold" data-testid="sales-note-new-btn">
          <ShoppingCart className="h-4 w-4 mr-1.5" /> Nueva nota de venta
        </Button>
      </div>
      <div className="pe-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-slate-50 text-slate-600 text-left text-xs uppercase tracking-wider">
              <th className="px-3 py-3">Folio</th>
              <th className="px-3 py-3">Fecha</th>
              <th className="px-3 py-3">Cliente</th>
              <th className="px-3 py-3">Concepto</th>
              <th className="px-3 py-3">Método</th>
              <th className="px-3 py-3 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center py-10 text-slate-400"><Loader2 className="animate-spin inline mr-2" />Cargando…</td></tr>
              : items.length === 0 ? <tr><td colSpan={6} className="text-center py-10 text-slate-400">Sin notas de venta todavía.</td></tr>
              : items.map((n) => (
                <tr key={n.id} data-testid={`sales-note-row-${n.folio}`} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-3 py-3 font-bold text-[#005A91]">{n.folio}</td>
                  <td className="px-3 py-3">{dateEs(n.date || n.created_at)}</td>
                  <td className="px-3 py-3">{n.client?.name || "—"}</td>
                  <td className="px-3 py-3 text-xs text-slate-600">
                    {(n.items || []).slice(0, 2).map((it) => it.description).join(" · ")}
                    {(n.items || []).length > 2 ? ` (+${n.items.length - 2})` : ""}
                  </td>
                  <td className="px-3 py-3 capitalize text-slate-600">{n.payment_method}</td>
                  <td className="px-3 py-3 text-right pe-numeric font-bold text-emerald-700">{money(n.total)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
      <SalesNoteNewDialog open={showNew} onOpenChange={setShowNew} onCreated={() => { setShowNew(false); load(); }} />
    </div>
  );
}

function SalesNoteNewDialog({ open, onOpenChange, onCreated }) {
  const emptyItem = { description: "", unit: "PZA", quantity: 1, unit_price: 0, discount_pct: 0 };
  const [clients, setClients] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("_new");
  const [client, setClient] = useState({ name: "", rfc: "", email: "", phone: "", address: "" });
  const [rows, setRows] = useState([{ ...emptyItem }]);
  const [ivaRate, setIvaRate] = useState(16);
  const [paymentMethod, setPaymentMethod] = useState("efectivo");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try { const r = await api.get("/clients"); setClients(r.data || []); }
      catch { setClients([]); }
    })();
  }, [open]);

  const reset = () => {
    setSelectedClientId("_new");
    setClient({ name: "", rfc: "", email: "", phone: "", address: "" });
    setRows([{ ...emptyItem }]);
    setIvaRate(16); setPaymentMethod("efectivo"); setNotes("");
  };

  useEffect(() => {
    if (selectedClientId === "_new") return;
    const c = clients.find((x) => x.id === selectedClientId);
    if (c) setClient({
      name: c.name || "", rfc: c.rfc || "", email: c.email || "",
      phone: c.phone || "", address: c.address || "",
    });
  }, [selectedClientId, clients]);

  const addRow = () => setRows([...rows, { ...emptyItem }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, patch) => setRows(rows.map((r, idx) => idx === i ? { ...r, ...patch } : r));

  // Live totals (mirror the backend compute_totals logic)
  const subtotal = rows.reduce((acc, r) => {
    const lineBase = Number(r.quantity || 0) * Number(r.unit_price || 0);
    const disc = lineBase * (Number(r.discount_pct || 0) / 100);
    return acc + (lineBase - disc);
  }, 0);
  const iva = subtotal * (Number(ivaRate) / 100);
  const total = subtotal + iva;

  const save = async () => {
    if (!client.name) return toast.error("Falta el nombre del cliente");
    const validItems = rows.filter((r) => r.description && Number(r.quantity) > 0 && Number(r.unit_price) >= 0);
    if (validItems.length === 0) return toast.error("Agrega al menos un concepto válido");
    setSaving(true);
    try {
      const body = {
        client: {
          client_id: selectedClientId !== "_new" ? selectedClientId : undefined,
          name: client.name, rfc: client.rfc || undefined,
          email: client.email || undefined, phone: client.phone || undefined,
          address: client.address || undefined,
        },
        items: validItems.map((r) => ({
          description: r.description, unit: r.unit || "PZA",
          quantity: Number(r.quantity), unit_price: Number(r.unit_price),
          discount_pct: Number(r.discount_pct || 0),
        })),
        iva_rate: Number(ivaRate),
        payment_method: paymentMethod,
        notes: notes || undefined,
      };
      const r = await api.post("/sales-notes", body);
      toast.success(`Nota de venta ${r.data.folio} emitida — ingreso registrado`);
      reset();
      onCreated?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col p-0" data-testid="sales-note-dialog">
        <DialogHeader className="px-6 pt-6">
          <DialogTitle className="text-[#005A91] font-black text-xl">Nueva nota de venta</DialogTitle>
          <DialogDescription>
            El folio se asigna automáticamente y se registra un ingreso por el total.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
          {/* Cliente */}
          <div>
            <div className="text-[10px] font-black tracking-[0.2em] uppercase text-slate-500 mb-2">Cliente</div>
            <Select value={selectedClientId} onValueChange={setSelectedClientId}>
              <SelectTrigger data-testid="sn-client-select" className="h-10 mb-3"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_new">— Nuevo cliente / mostrador —</SelectItem>
                {clients.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name} {c.rfc ? `· ${c.rfc}` : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Nombre / Razón social *" full>
                <Input value={client.name} onChange={(e) => setClient({ ...client, name: e.target.value })} data-testid="sn-client-name" />
              </Field>
              <Field label="RFC">
                <Input value={client.rfc} onChange={(e) => setClient({ ...client, rfc: e.target.value })} data-testid="sn-client-rfc" />
              </Field>
              <Field label="Teléfono">
                <Input value={client.phone} onChange={(e) => setClient({ ...client, phone: e.target.value })} data-testid="sn-client-phone" />
              </Field>
              <Field label="Correo" full>
                <Input value={client.email} onChange={(e) => setClient({ ...client, email: e.target.value })} data-testid="sn-client-email" />
              </Field>
            </div>
          </div>

          {/* Conceptos */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-black tracking-[0.2em] uppercase text-slate-500">Conceptos</div>
              <Button size="sm" variant="outline" onClick={addRow} data-testid="sn-add-row"
                      className="h-8 border-[#005A91]/30 text-[#005A91]">
                <Plus className="h-3.5 w-3.5 mr-1" /> Agregar
              </Button>
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => {
                const lineBase = Number(r.quantity || 0) * Number(r.unit_price || 0);
                const importe = lineBase - lineBase * (Number(r.discount_pct || 0) / 100);
                return (
                  <div key={i} className="grid grid-cols-12 gap-2 items-start p-2 rounded-md border border-slate-200 bg-slate-50" data-testid={`sn-row-${i}`}>
                    <div className="col-span-12 sm:col-span-5">
                      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Descripción</Label>
                      <Input value={r.description} onChange={(e) => updateRow(i, { description: e.target.value })}
                              data-testid={`sn-row-desc-${i}`} className="h-9 mt-1" />
                    </div>
                    <div className="col-span-4 sm:col-span-1">
                      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Unidad</Label>
                      <Input value={r.unit} onChange={(e) => updateRow(i, { unit: e.target.value.toUpperCase() })}
                              data-testid={`sn-row-unit-${i}`} className="h-9 mt-1" />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Cantidad</Label>
                      <Input type="number" step="0.01" value={r.quantity}
                              onChange={(e) => updateRow(i, { quantity: e.target.value })}
                              data-testid={`sn-row-qty-${i}`} className="h-9 mt-1 text-right pe-numeric" />
                    </div>
                    <div className="col-span-4 sm:col-span-2">
                      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">P. unitario</Label>
                      <Input type="number" step="0.01" value={r.unit_price}
                              onChange={(e) => updateRow(i, { unit_price: e.target.value })}
                              data-testid={`sn-row-price-${i}`} className="h-9 mt-1 text-right pe-numeric" />
                    </div>
                    <div className="col-span-10 sm:col-span-2 flex flex-col justify-end">
                      <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">Importe</Label>
                      <div className="h-9 mt-1 px-2 rounded border border-slate-200 bg-white flex items-center justify-end font-bold pe-numeric text-[#005A91]">
                        {money(importe)}
                      </div>
                    </div>
                    <div className="col-span-2 sm:col-span-12 flex justify-end sm:justify-start">
                      {rows.length > 1 && (
                        <Button size="sm" variant="ghost" onClick={() => removeRow(i)}
                                data-testid={`sn-remove-${i}`}
                                className="h-8 text-slate-400 hover:text-red-700 hover:bg-red-50">
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Config extra + Totales */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="space-y-3">
              <Field label="IVA %">
                <Input type="number" step="0.01" value={ivaRate} onChange={(e) => setIvaRate(e.target.value)} data-testid="sn-iva" />
              </Field>
              <Field label="Método de pago">
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger data-testid="sn-method"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAY_METHODS.map((m) => <SelectItem key={m} value={m} className="capitalize">{m}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Notas (opcional)">
                <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="sn-notes"
                          placeholder="Si dejas vacío se usa la nota predeterminada de 'producto'." />
              </Field>
            </div>
            <div className="bg-[#005A91]/5 border border-[#005A91]/20 rounded-md p-4 space-y-2 text-sm h-fit">
              <div className="flex justify-between text-slate-600"><span>Subtotal</span><span className="pe-numeric font-bold" data-testid="sn-subtotal">{money(subtotal)}</span></div>
              <div className="flex justify-between text-slate-600"><span>IVA ({ivaRate}%)</span><span className="pe-numeric font-bold" data-testid="sn-iva-amount">{money(iva)}</span></div>
              <div className="border-t border-[#005A91]/20 my-1"></div>
              <div className="flex justify-between text-[#005A91]"><span className="font-black">TOTAL</span><span className="pe-numeric font-black text-lg" data-testid="sn-total">{money(total)}</span></div>
              <div className="text-[10px] text-slate-500 mt-2 italic">
                Al guardar se registra automáticamente un ingreso de {money(total)}.
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="px-6 py-4 border-t border-slate-200 bg-slate-50 sm:justify-between">
          <div className="text-xs text-slate-500">
            El folio se asignará como <b className="text-[#005A91]">NV-{new Date().getFullYear()}-XXXX</b>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button onClick={save} disabled={saving || total <= 0} data-testid="sn-save"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Emitir nota de venta
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============ MAIN PAGE ============
export default function Finance() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = params.get("tab") || "dashboard";
  const setTab = (t) => setParams({ tab: t });

  return (
    <div className="space-y-6 max-w-6xl" data-testid="finance-page">
      <div>
        <div className="text-[11px] font-black tracking-[0.24em] uppercase text-[#005A91]">Módulo 5</div>
        <h1 className="text-3xl lg:text-4xl font-black text-[#0A1118] tracking-tight flex items-center gap-3">
          <DollarSign className="h-8 w-8 text-[#005A91]" />
          Finanzas
        </h1>
        <p className="text-slate-500 mt-1 text-sm">
          Flujo de efectivo, gastos con OCR, ingresos y nómina — con revisión antes de sumar al reporte final.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-3xl">
          <TabsTrigger value="dashboard" data-testid="tab-dashboard">Panel</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses">Gastos</TabsTrigger>
          <TabsTrigger value="income" data-testid="tab-income">Ingresos</TabsTrigger>
          <TabsTrigger value="sales-notes" data-testid="tab-sales-notes">Notas de venta</TabsTrigger>
          <TabsTrigger value="payroll" data-testid="tab-payroll">Nómina</TabsTrigger>
        </TabsList>
        <TabsContent value="dashboard" className="mt-6"><DashboardTab onGoTo={setTab} /></TabsContent>
        <TabsContent value="expenses" className="mt-6"><ExpensesTab /></TabsContent>
        <TabsContent value="income" className="mt-6"><IncomeTab /></TabsContent>
        <TabsContent value="sales-notes" className="mt-6"><SalesNotesTab /></TabsContent>
        <TabsContent value="payroll" className="mt-6"><PayrollTab /></TabsContent>
      </Tabs>
    </div>
  );
}
